import React, { useState, useEffect } from 'react';
import styles from './Dashboard.module.css';
import { Line, Bar, Pie } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement
} from 'chart.js';
import { Typography, Card, CardContent, Box, LinearProgress, Grid, Paper } from '@mui/material';
import { Table, TableHead, TableBody, TableCell, TableRow, TableContainer } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PeopleIcon from '@mui/icons-material/People';
import InventoryIcon from '@mui/icons-material/Inventory';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PendingIcon from '@mui/icons-material/Pending';
import CancelIcon from '@mui/icons-material/Cancel';
import { Sale, Purchase, Supplier, salesData, purchaseData, supplierData } from '../../data/dummyData';

// Register ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
);

interface RecentActivity {
    type: 'sale' | 'purchase';
    amount: number;
    date: string;
}

// Dummy data for stats - replace with real data later
const statsData = {
    totalSales: {
        value: 24780,
        change: 12.5,
    },
    totalOrders: {
        value: 1245,
        change: -2.3,
    },
    products: {
        value: 856,
    },
    revenueGrowth: {
        value: 18.2,
        change: 4.1,
    },
};

// Dummy data for top products
const topProductsData = [
    { id: 1, name: 'Product 1', category: 'Category A', value: 1234, change: 12 },
    { id: 2, name: 'Product 2', category: 'Category B', value: 1234, change: 12 },
    { id: 3, name: 'Product 3', category: 'Category A', value: 1234, change: 12 },
    { id: 4, name: 'Product 4', category: 'Category C', value: 1234, change: 12 },
    { id: 5, name: 'Product 5', category: 'Category B', value: 1234, change: 12 },
];

// Helper component for Stat Card
interface StatCardProps {
    title: string;
    value: string | number;
    change?: number;
    icon: React.ElementType;
    iconBgColor: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, change, icon: Icon, iconBgColor }) => {
    const isPositive = change !== undefined && change >= 0;
    const isNegative = change !== undefined && change < 0;
    const changeText = change !== undefined ? `${isPositive ? '+' : ''}${change}% from last month` : null;

    return (
        <div className={styles.statCard}>
            <div className={styles.statInfo}>
                <p className={styles.statTitle}>{title}</p>
                <p className={styles.statValue}>{value}</p>
                {changeText && (
                    <p className={`${styles.statChange} ${isPositive ? styles.positive : styles.negative}`}>
                        {changeText}
                    </p>
                )}
            </div>
            <div className={styles.statIconWrapper} style={{ backgroundColor: iconBgColor }}>
                <Icon className={styles.statIcon} />
            </div>
        </div>
    );
};

const Dashboard: React.FC = () => {
    const [salesTrend, setSalesTrend] = useState<number[]>([]);
    const [topProducts, setTopProducts] = useState<{ name: string; sales: number }[]>([]);
    const [recentActivities, setRecentActivities] = useState<RecentActivity[]>([]);

    useEffect(() => {
        // Calculate sales trend for the last 7 days
        const last7Days = Array(7).fill(0);
        const today = new Date();
        salesData.forEach((sale: Sale) => {
            const saleDate = new Date(sale.date);
            const diffDays = Math.floor((today.getTime() - saleDate.getTime()) / (1000 * 60 * 60 * 24));
            if (diffDays < 7) {
                last7Days[6 - diffDays] += sale.totalAmount;
            }
        });
        setSalesTrend(last7Days);

        // Calculate top products
        const productSales: { [key: string]: number } = {};
        salesData.forEach((sale: Sale) => {
            sale.items.forEach((item: { name: string; quantity: number }) => {
                productSales[item.name] = (productSales[item.name] || 0) + item.quantity;
            });
        });
        const topProductsList = Object.entries(productSales)
            .map(([name, sales]) => ({ name, sales }))
            .sort((a, b) => b.sales - a.sales)
            .slice(0, 5);
        setTopProducts(topProductsList);

        // Get recent activities
        const activities = [
            ...salesData.slice(-5).map((sale: Sale) => ({
                type: 'sale' as const,
                amount: sale.totalAmount,
                date: sale.date
            })),
            ...purchaseData.slice(-5).map((purchase: Purchase) => ({
                type: 'purchase' as const,
                amount: purchase.totalAmount,
                date: purchase.date
            }))
        ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
         .slice(0, 5);
        setRecentActivities(activities);
    }, []);

    const totalSales = salesData.reduce((sum: number, sale: Sale) => sum + sale.totalAmount, 0);
    const totalPurchases = purchaseData.reduce((sum: number, po: Purchase) => sum + po.totalAmount, 0);
    const activeSuppliers = supplierData.filter((s: Supplier) => s.status === 'Active').length;
    const totalProducts = new Set(salesData.flatMap((sale: Sale) => sale.items.map(item => item.name))).size;

    const salesChartData = {
        labels: ['6 days ago', '5 days ago', '4 days ago', '3 days ago', '2 days ago', 'Yesterday', 'Today'],
        datasets: [
            {
                label: 'Sales',
                data: salesTrend,
                borderColor: '#2563eb',
                backgroundColor: '#2563eb20',
                tension: 0.4,
                fill: true
            }
        ]
    };

    const topProductsData = {
        labels: topProducts.map(product => product.name),
        datasets: [
            {
                label: 'Units Sold',
                data: topProducts.map(product => product.sales),
                backgroundColor: [
                    'rgba(255, 99, 132, 0.5)',
                    'rgba(54, 162, 235, 0.5)',
                    'rgba(255, 206, 86, 0.5)',
                    'rgba(75, 192, 192, 0.5)',
                    'rgba(153, 102, 255, 0.5)'
                ]
            }
        ]
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                mode: 'index' as const,
                intersect: false,
                callbacks: {
                    label: function(context: any) {
                        return `$${context.parsed.y.toLocaleString()}`;
                    }
                }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                grid: {
                    display: false
                },
                ticks: {
                    callback: function(value: any) {
                        return '$' + value.toLocaleString();
                    }
                }
            },
            x: {
                grid: {
                    display: false
                }
            }
        }
    };

    return (
        <div className={styles.dashboard}>
            <Typography variant="h4" className={styles.title}>
                Dashboard
            </Typography>

            <Grid container spacing={3} className={styles.statsGrid}>
                <Grid component="div" sx={{ gridColumn: { xs: 'span 12', sm: 'span 6', md: 'span 3' } }}>
                    <StatCard
                        title="Total Sales"
                        value={`$${totalSales.toLocaleString()}`}
                        icon={TrendingUpIcon}
                        iconBgColor="#2563eb"
                        change={12.5}
                    />
                </Grid>
                <Grid component="div" sx={{ gridColumn: { xs: 'span 12', sm: 'span 6', md: 'span 3' } }}>
                    <StatCard
                        title="Total Purchases"
                        value={`$${totalPurchases.toLocaleString()}`}
                        icon={ShoppingCartIcon}
                        iconBgColor="#10b981"
                        change={8.2}
                    />
                </Grid>
                <Grid component="div" sx={{ gridColumn: { xs: 'span 12', sm: 'span 6', md: 'span 3' } }}>
                    <StatCard
                        title="Active Suppliers"
                        value={activeSuppliers}
                        icon={PeopleIcon}
                        iconBgColor="#f59e0b"
                        change={5.7}
                    />
                </Grid>
                <Grid component="div" sx={{ gridColumn: { xs: 'span 12', sm: 'span 6', md: 'span 3' } }}>
                    <StatCard
                        title="Total Products"
                        value={totalProducts}
                        icon={InventoryIcon}
                        iconBgColor="#8b5cf6"
                        change={3.4}
                    />
                </Grid>
            </Grid>

            <Grid container spacing={3} className={styles.chartsGrid}>
                <Grid component="div" sx={{ gridColumn: { xs: 'span 12', md: 'span 8' } }}>
                    <Card className={styles.chartCard}>
                        <CardContent>
                            <Typography variant="h6" className={styles.chartTitle}>
                                Sales Trend
                            </Typography>
                            <Box className={styles.chartContainer}>
                                <Line data={salesChartData} options={chartOptions} />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid component="div" sx={{ gridColumn: { xs: 'span 12', md: 'span 4' } }}>
                    <Card className={styles.chartCard}>
                        <CardContent>
                            <Typography variant="h6" className={styles.chartTitle}>
                                Top Products
                            </Typography>
                            <Box className={styles.productsList}>
                                {topProducts.map((product, index) => (
                                    <Box key={index} className={styles.productItem}>
                                        <Box className={styles.productInfo}>
                                            <Typography variant="body2" className={styles.productName}>
                                                {product.name}
                                            </Typography>
                                            <Box className={styles.productBar}>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={(product.sales / topProducts[0].sales) * 100}
                                                    className={styles.productProgress}
                                                />
                                                <Typography variant="body2" className={styles.productSales}>
                                                    {product.sales} units
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Card className={styles.activitiesCard}>
                <CardContent>
                    <Typography variant="h6" className={styles.activitiesTitle}>
                        Recent Activities
                    </Typography>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Amount</TableCell>
                                    <TableCell>Date</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {recentActivities.map((activity, index) => (
                                    <TableRow key={index}>
                                        <TableCell>
                                            <Box className={styles.activityType}>
                                                {activity.type === 'sale' ? (
                                                    <TrendingUpIcon className={styles.saleIcon} />
                                                ) : (
                                                    <ShoppingCartIcon className={styles.purchaseIcon} />
                                                )}
                                                <Typography variant="body2">
                                                    {activity.type.charAt(0).toUpperCase() + activity.type.slice(1)}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>${activity.amount.toLocaleString()}</TableCell>
                                        <TableCell>{new Date(activity.date).toLocaleDateString()}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>
        </div>
    );
};

export default Dashboard; 